import { Injectable } from '@angular/core';
import { EncryptStorage } from 'encrypt-storage';
import { environment } from '../../environments/environment';

function getStorageSecretKey(): string | null {
    const runtimeKey = environment.storageSecretKey?.trim();
    if (runtimeKey && runtimeKey.length >= 10) {
        return runtimeKey;
    }

    console.error('[runtime-config] Missing or invalid storageSecretKey. Encrypted local storage persistence is disabled.');
    return null;
}

@Injectable({
    providedIn: 'root'
})
class StorageService {
    private readonly storage: EncryptStorage | null;

    constructor() {
        const storageSecretKey = getStorageSecretKey();
        this.storage = storageSecretKey
            ? new EncryptStorage(storageSecretKey, {
                prefix: 'cadreWeb',
                storageType: 'localStorage',
                encAlgorithm: 'AES',
                doNotEncryptValues: false
            })
            : null;
    }

    hash(key: string): string {
        return this.storage?.hash(key) ?? key;
    }

    setItem<T>(key: string, value: T): void {
        this.storage?.setItem(key, value);
    }

    getItem<T>(key: string): T | undefined {
        return this.storage?.getItem<T>(key);
    }

    removeItem(key: string): void {
        this.storage?.removeItem(key);
    }

    clear(): void {
        this.storage?.clear();
    }
}

@Injectable({
    providedIn: 'root'
})
export class LocalStorageService {
    private static readonly sensitiveKeys = new Set<string>([
        'userDetails',
        'role',
        'verticle',
        'fullUserRoleVerticle',
        'selectedRoleWithGeo'
    ]);

    private static readonly transientStore = new Map<string, unknown>();

    constructor(private storageService: StorageService) { }

    set<T>(key: string, value: T) {
        if (LocalStorageService.sensitiveKeys.has(key)) {
            LocalStorageService.transientStore.set(key, value);
            this.storageService.removeItem(this.storageService.hash(key));
            return;
        }
        this.storageService.setItem(this.storageService.hash(key), value);
    }

    get<T>(key: string) {
        if (LocalStorageService.sensitiveKeys.has(key)) {
            return LocalStorageService.transientStore.get(key) as T;
        }
        return this.storageService.getItem<T>(this.storageService.hash(key));
    }

    remove(key: string) {
        if (LocalStorageService.sensitiveKeys.has(key)) {
            LocalStorageService.transientStore.delete(key);
        }
        return this.storageService.removeItem(this.storageService.hash(key));
    }

    clearStorage() {
        LocalStorageService.transientStore.clear();
        this.storageService.clear();
    }

    clearSessionStorage(): void {
        sessionStorage.clear();
    }

}
